/**
 * Typed fetchers for /api/v1/archive — archival containers, search, retrieval,
 * retired-system datasets and the exit package.
 *
 * Types come from the OpenAPI-generated schema; regenerate with `pnpm gen:api`
 * after a backend Pydantic change. The schema names are generic
 * (`ContainerOut`, `SearchOut`, …) because that is what the router declares, so
 * they are re-exported here under archive-specific aliases to keep call sites
 * unambiguous.
 */

import type { components } from "./_generated/schema";
import { apiFetch } from "./apiFetch";

export type ArchiveContainer = components["schemas"]["ContainerOut"];
export type ArchiveContainerList = components["schemas"]["ContainerListOut"];
export type ArchiveContainerIn = components["schemas"]["ContainerIn"];
export type ArchiveContainerPatch = components["schemas"]["ContainerPatch"];
export type ArchiveField = components["schemas"]["FieldOut"];
export type ArchiveSearchOut = components["schemas"]["SearchOut"];
export type ArchiveSearchHit = components["schemas"]["SearchHit"];
export type ArchiveFacet = components["schemas"]["FacetOut"];
export type ArchivedObjectOut = components["schemas"]["ObjectOut"];
export type ArchiveDataset = components["schemas"]["DatasetOut"];
export type ArchiveDatasetList = components["schemas"]["DatasetListOut"];
export type ArchiveDatasetSchema = components["schemas"]["SchemaOut"];
export type ArchiveRowsOut = components["schemas"]["RowsOut"];
export type ArchiveLegalHoldOut = components["schemas"]["LegalHoldOut"];

const BASE = "/api/v1/archive";

export interface SearchParams {
  q?: string;
  containerId?: string | null;
  facets?: Record<string, string[]>;
  dateFrom?: string | null;
  dateTo?: string | null;
  sort?: string;
  page?: number;
  pageSize?: number;
}

function searchQuery(params: SearchParams): string {
  const sp = new URLSearchParams();
  if (params.q?.trim()) sp.set("q", params.q.trim());
  if (params.containerId) sp.set("container_id", params.containerId);
  if (params.dateFrom) sp.set("date_from", params.dateFrom);
  if (params.dateTo) sp.set("date_to", params.dateTo);
  sp.set("sort", params.sort ?? "relevance");
  sp.set("page", String(params.page ?? 1));
  sp.set("page_size", String(params.pageSize ?? 25));
  for (const [key, values] of Object.entries(params.facets ?? {})) {
    for (const value of values) sp.append(`facet.${key}`, value);
  }
  return sp.toString();
}

export function listArchiveContainers(token: string): Promise<ArchiveContainerList> {
  return apiFetch<ArchiveContainerList>(`${BASE}/containers`, { token });
}

/** Classifications this tenant already uses, for the creatable picker. The
 *  taxonomy is the customer's, so it is derived from their archives rather
 *  than from a list we ship. */
export type ArchiveUploadResult = components["schemas"]["UploadResultOut"];

/** Add documents to an archive by hand.
 *
 *  `metadata` is positional — entry *i* describes file *i* — so one submission
 *  can describe each document differently, which is what records work actually
 *  looks like. Sent as multipart, not JSON: the files are the payload and
 *  base64-ing them into a JSON body would inflate every upload by a third. */
export function uploadArchiveDocuments(
  token: string,
  containerId: string,
  files: File[],
  metadata: Record<string, string>[],
): Promise<ArchiveUploadResult> {
  const form = new FormData();
  for (const file of files) form.append("files", file);
  form.append("metadata", JSON.stringify(metadata));
  return apiFetch<ArchiveUploadResult>(`${BASE}/containers/${containerId}/upload`, {
    token,
    method: "POST",
    body: form,
  });
}

export function listArchiveClusters(token: string): Promise<string[]> {
  return apiFetch<string[]>(`${BASE}/clusters`, { token });
}

export function getArchiveContainer(token: string, containerId: string): Promise<ArchiveContainer> {
  return apiFetch<ArchiveContainer>(`${BASE}/containers/${containerId}`, { token });
}

export function createArchiveContainer(
  token: string,
  body: ArchiveContainerIn,
): Promise<ArchiveContainer> {
  return apiFetch<ArchiveContainer>(`${BASE}/containers`, { token, method: "POST", json: body });
}

export function updateArchiveContainer(
  token: string,
  containerId: string,
  body: ArchiveContainerPatch,
): Promise<ArchiveContainer> {
  return apiFetch<ArchiveContainer>(`${BASE}/containers/${containerId}`, {
    token,
    method: "PATCH",
    json: body,
  });
}

export function setArchiveLegalHold(
  token: string,
  containerId: string,
  hold: boolean,
  reason: string,
): Promise<ArchiveLegalHoldOut> {
  const qs = new URLSearchParams({ hold: String(hold), reason });
  return apiFetch<ArchiveLegalHoldOut>(`${BASE}/containers/${containerId}/legal-hold?${qs}`, {
    token,
    method: "POST",
  });
}

export function searchArchive(token: string, params: SearchParams): Promise<ArchiveSearchOut> {
  return apiFetch<ArchiveSearchOut>(`${BASE}/search?${searchQuery(params)}`, { token });
}

export function listArchiveFacets(
  token: string,
  containerId?: string | null,
): Promise<ArchiveFacet[]> {
  const qs = containerId ? `?container_id=${encodeURIComponent(containerId)}` : "";
  return apiFetch<ArchiveFacet[]>(`${BASE}/facets${qs}`, { token });
}

export function getArchivedObject(token: string, objectId: string): Promise<ArchivedObjectOut> {
  return apiFetch<ArchivedObjectOut>(`${BASE}/objects/${objectId}`, { token });
}

export function listArchiveDatasets(
  token: string,
  containerId: string,
): Promise<ArchiveDatasetList> {
  return apiFetch<ArchiveDatasetList>(`${BASE}/containers/${containerId}/datasets`, { token });
}

export function getArchiveDatasetSchema(
  token: string,
  datasetId: string,
): Promise<ArchiveDatasetSchema> {
  return apiFetch<ArchiveDatasetSchema>(`${BASE}/datasets/${datasetId}/schema`, { token });
}

export function getArchiveDatasetManifest(
  token: string,
  datasetId: string,
): Promise<Record<string, unknown>> {
  return apiFetch<Record<string, unknown>>(`${BASE}/datasets/${datasetId}/manifest`, { token });
}

export interface RowFilter {
  column: string;
  op: string;
  value: string;
}

function rowsQuery(filters: RowFilter[], q: string, page: number, pageSize: number): string {
  const sp = new URLSearchParams();
  for (const f of filters) sp.append(`filter.${f.column}`, `${f.op}:${f.value}`);
  if (q.trim()) sp.set("q", q.trim());
  sp.set("page", String(page));
  sp.set("page_size", String(pageSize));
  return sp.toString();
}

export function getArchiveDatasetRows(
  token: string,
  datasetId: string,
  filters: RowFilter[],
  q = "",
  page = 1,
  pageSize = 50,
): Promise<ArchiveRowsOut> {
  return apiFetch<ArchiveRowsOut>(
    `${BASE}/datasets/${datasetId}/rows?${rowsQuery(filters, q, page, pageSize)}`,
    { token },
  );
}

/** Fetch bytes with the bearer token and hand the browser a download. */
async function downloadBlob(url: string, token: string, method: "GET" | "POST", fallback: string) {
  const res = await fetch(url, { method, headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const name = match?.[1] ? decodeURIComponent(match[1]) : fallback;
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(href);
}

export function retrieveArchivedObject(token: string, objectId: string, fallback: string) {
  return downloadBlob(
    `${BASE}/objects/${objectId}/content?disposition=attachment`,
    token,
    "GET",
    fallback,
  );
}

export function exportArchiveDataset(
  token: string,
  datasetId: string,
  filters: RowFilter[],
  q: string,
  fallback: string,
) {
  const sp = new URLSearchParams();
  for (const f of filters) sp.append(`filter.${f.column}`, `${f.op}:${f.value}`);
  if (q.trim()) sp.set("q", q.trim());
  return downloadBlob(`${BASE}/datasets/${datasetId}/export?${sp}`, token, "GET", fallback);
}

export function exportArchiveContainer(token: string, containerId: string, fallback: string) {
  return downloadBlob(`${BASE}/containers/${containerId}/export`, token, "POST", fallback);
}

/** Inline preview URL — the drawer fetches it with the token via blob URL. */
export async function objectPreviewUrl(token: string, objectId: string): Promise<string> {
  const res = await fetch(`${BASE}/objects/${objectId}/content?disposition=inline`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Preview failed (${res.status})`);
  return URL.createObjectURL(await res.blob());
}

// --- Access grants -------------------------------------------------------

export type ArchiveGrant = components["schemas"]["ArchiveGrantOut"];
export type ArchiveGrantList = components["schemas"]["ArchiveGrantListOut"];
export type ArchiveGrantCandidate = components["schemas"]["ArchiveGrantCandidate"];
export type ArchiveGrantCandidateList = components["schemas"]["ArchiveGrantCandidateListOut"];

export function listArchiveGrants(token: string, containerId: string): Promise<ArchiveGrantList> {
  return apiFetch<ArchiveGrantList>(`${BASE}/containers/${containerId}/grants`, { token });
}

export function listArchiveGrantCandidates(
  token: string,
  containerId: string,
): Promise<ArchiveGrantCandidateList> {
  return apiFetch<ArchiveGrantCandidateList>(`${BASE}/containers/${containerId}/grant-candidates`, {
    token,
  });
}

/** `read` — search the archive and see a record's metadata.
 *  `retrieve` — additionally take the content out: open a document, export rows. */
export const ARCHIVE_PERMISSIONS = ["read", "retrieve"] as const;
export type ArchivePermission = (typeof ARCHIVE_PERMISSIONS)[number];

export function grantArchiveAccess(
  token: string,
  containerId: string,
  userId: string,
  permission: ArchivePermission = "read",
): Promise<ArchiveGrant> {
  return apiFetch<ArchiveGrant>(`${BASE}/containers/${containerId}/grants`, {
    token,
    method: "POST",
    json: { user_id: userId, permission },
  });
}

/** Raise or lower what an existing grant permits, without revoking it — the
 *  trail then reads as a permission change rather than a loss of access. */
export function updateArchiveGrant(
  token: string,
  containerId: string,
  userId: string,
  permission: ArchivePermission,
): Promise<ArchiveGrant> {
  return apiFetch<ArchiveGrant>(`${BASE}/containers/${containerId}/grants/${userId}`, {
    token,
    method: "PATCH",
    json: { permission },
  });
}

/** `reason` is required by the API — a withdrawal of access with no stated
 *  reason is not defensible in an inspection. */
export function revokeArchiveAccess(
  token: string,
  containerId: string,
  userId: string,
  reason: string,
): Promise<void> {
  const qs = new URLSearchParams({ reason });
  return apiFetch<void>(`${BASE}/containers/${containerId}/grants/${userId}?${qs}`, {
    token,
    method: "DELETE",
  });
}

// --- Record views and joins ----------------------------------------------

export type ArchiveRecordView = components["schemas"]["RecordViewOut"];
export type ArchiveRecordViewList = components["schemas"]["RecordViewListOut"];
export type ArchiveRecordViewIn = components["schemas"]["RecordViewIn"];
export type ArchiveJoinedRows = components["schemas"]["JoinedRowsOut"];
export type ArchiveJoinOverlap = components["schemas"]["OverlapOut"];
export type ArchiveAdHocJoin = components["schemas"]["AdHocJoinIn"];

export function listRecordViews(
  token: string,
  containerId: string,
): Promise<ArchiveRecordViewList> {
  return apiFetch<ArchiveRecordViewList>(`${BASE}/containers/${containerId}/record-views`, {
    token,
  });
}

export function createRecordView(
  token: string,
  containerId: string,
  body: ArchiveRecordViewIn,
): Promise<ArchiveRecordView> {
  return apiFetch<ArchiveRecordView>(`${BASE}/containers/${containerId}/record-views`, {
    token,
    method: "POST",
    json: body,
  });
}

export function deleteRecordView(
  token: string,
  containerId: string,
  viewId: string,
): Promise<void> {
  return apiFetch<void>(`${BASE}/containers/${containerId}/record-views/${viewId}`, {
    token,
    method: "DELETE",
  });
}

export function getRecordViewRows(
  token: string,
  viewId: string,
  filters: RowFilter[],
  q = "",
  page = 1,
  pageSize = 50,
): Promise<ArchiveJoinedRows> {
  return apiFetch<ArchiveJoinedRows>(
    `${BASE}/record-views/${viewId}/rows?${rowsQuery(filters, q, page, pageSize)}`,
    { token },
  );
}

export function previewJoin(token: string, body: ArchiveAdHocJoin): Promise<ArchiveJoinedRows> {
  return apiFetch<ArchiveJoinedRows>(`${BASE}/joins/preview`, {
    token,
    method: "POST",
    json: body,
  });
}

/** How many base rows find a match — surfaced before a view is saved so a
 *  mistyped key is caught there, not as an empty grid in front of a user. */
export function joinOverlap(token: string, body: ArchiveAdHocJoin): Promise<ArchiveJoinOverlap> {
  return apiFetch<ArchiveJoinOverlap>(`${BASE}/joins/overlap`, {
    token,
    method: "POST",
    json: body,
  });
}

// --- Natural-language query ----------------------------------------------

export type ArchiveNlQueryOut = components["schemas"]["NlQueryOut"];

/** Returns the interpreted query, not results — the caller runs it through the
 *  normal search endpoints so grants are applied by the same code as a
 *  hand-built filter. `available: false` means fall back to text search. */
export function archiveNlQuery(
  token: string,
  question: string,
  containerIds: string[] = [],
): Promise<ArchiveNlQueryOut> {
  return apiFetch<ArchiveNlQueryOut>(`${BASE}/nl-query`, {
    token,
    method: "POST",
    json: { question, container_ids: containerIds },
  });
}
